import Link from 'next/link'
import { Children, Fragment, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

const FRAME_HEIGHT = {
    screen: 'min-h-dvh',
    section: 'min-h-[60vh]',
} as const

const ACTION_CLASS = 'text-body-fg hover:text-action hover:underline'

/**
 * Renders the editorial frame shared by the app's error and not-found
 * boundaries: a marginalia eyebrow, a display headline, one italic line
 * of copy, and a hairline-bracketed row of actions.
 *
 * @remarks
 * No hooks and no `'use client'`, so server `not-found.tsx` files and client
 * `error.tsx` files can both render it. Error reporting lives in
 * `useReportRouteError` so the not-found pages never import posthog.
 */
export function RouteBoundary({
    eyebrow,
    title,
    children,
    actions,
    height = 'screen',
    className,
}: {
    eyebrow: ReactNode
    title: ReactNode
    /** The italic message line under the headline. */
    children: ReactNode
    /** Rendered left to right, each bracketed by hairlines. */
    actions: ReactNode[]
    /**
     * `screen` fills the viewport; `section` sits inside a chromed layout
     * whose strip already takes vertical space.
     */
    height?: keyof typeof FRAME_HEIGHT
    className?: string
}) {
    return (
        <div
            className={cn(
                'flex items-center justify-center bg-paper px-5 py-16 sm:px-8',
                FRAME_HEIGHT[height],
                className
            )}
        >
            <div className="flex w-full max-w-md flex-col items-center gap-8 text-center">
                <p className="text-meta text-muted-fg uppercase tabular-nums">
                    {eyebrow}
                </p>
                <h1 className="font-serif text-display tracking-tight text-ink">
                    {title}
                </h1>
                <p className="max-w-[36ch] font-serif text-base leading-relaxed text-body-fg italic sm:text-lg">
                    {children}
                </p>
                <div className="my-4 flex w-full items-center gap-3 font-serif text-[13px] text-muted-fg italic">
                    <span className="h-px flex-1 bg-rule" aria-hidden />
                    {Children.map(actions, (action) => (
                        <Fragment>
                            {action}
                            <span className="h-px flex-1 bg-rule" aria-hidden />
                        </Fragment>
                    ))}
                </div>
            </div>
        </div>
    )
}

/**
 * Renders a boundary action: a link when given `href`, otherwise a button.
 */
export function BoundaryAction(
    props:
        | { href: string; children: ReactNode }
        | { onClick: () => void; children: ReactNode }
) {
    if ('href' in props) {
        return (
            <Link href={props.href} className={ACTION_CLASS}>
                {props.children}
            </Link>
        )
    }
    return (
        <button type="button" onClick={props.onClick} className={ACTION_CLASS}>
            {props.children}
        </button>
    )
}
